import React, { useState, useCallback, useEffect } from 'react';
import { Alert } from '@mui/material';
import ManualGoalModal from './ManualGoalModal';
import api from '../../services/api';

/**
 * GoalManagementSystem Component
 * Manages all goal-related functionality for therapist conversations
 * 
 * @param {Object} props - Component props
 * @param {Object} props.socket - Socket.io connection
 * @param {string} props.token - Authentication token
 * @param {Object} props.selectedConversation - Current conversation
 * @param {string} props.currentMode - Current conversation mode ('ai', 'human', 'assisted')
 * @param {string} props.userRole - User role (must be 'therapist')
 * @param {boolean} props.isSending - Whether a message is currently being sent
 * @param {string} props.currentSuggestion - Current AI suggestion text
 * @param {Object} props.currentAssessmentSuggestion - Current assessment suggestion if any
 * @param {string} props.clientName - Name of the client
 * @param {Function} props.onGoalProposalReceived - Called when AI goal proposal is received
 * @param {Function} props.onSendMessage - Called to send a message
 * @param {Function} props.onError - Called when an error occurs
 * @param {Function} props.onSuccess - Called when an action succeeds
 * @param {Function} props.onGoalProposalHandled - Called when goal proposal is handled
 * @returns {JSX.Element} GoalManagementSystem component
 */
const GoalManagementSystem = React.forwardRef((props, ref) => {
  const {
    socket,
    token,
    selectedConversation,
    currentMode,
    userRole,
    isSending,
    currentSuggestion,
    currentAssessmentSuggestion,
    clientName,
    onGoalProposalReceived,
    onSendMessage,
    onError,
    onSuccess,
    onGoalProposalHandled,
    onCanProposeManualGoalChange
  } = props;

  // AI Goal Proposal State
  const [pendingGoalProposal, setPendingGoalProposal] = useState(null);
  const [goalProposalActionLoading, setGoalProposalActionLoading] = useState(false);
  const [goalProposalActionError, setGoalProposalActionError] = useState('');
  const [isEditingGoalProposal, setIsEditingGoalProposal] = useState(false);
  const [editedGoalDescription, setEditedGoalDescription] = useState('');
  const [editedGoalType, setEditedGoalType] = useState('one_off');

  // Manual Goal Proposal State
  const [isManualGoalModalOpen, setIsManualGoalModalOpen] = useState(false);
  const [manualGoalDescription, setManualGoalDescription] = useState('');
  const [manualGoalType, setManualGoalType] = useState('one_off');
  const [manualGoalLoading, setManualGoalLoading] = useState(false);
  const [manualGoalError, setManualGoalError] = useState('');

  // Handle AI goal proposal received
  const handleAiGoalProposal = useCallback((data) => {
    const structuredData = {
      originalAiText: data.originalAiText,
      goalDetails: data.goalDetails,
      originalFunctionCall: data.originalFunctionCall 
    };
    setPendingGoalProposal(structuredData);
    
    if (onGoalProposalReceived) {
      onGoalProposalReceived(data);
    }
  }, [onGoalProposalReceived]);

  // Clear goal proposal
  const clearGoalProposal = useCallback(() => {
    setPendingGoalProposal(null);
    setIsEditingGoalProposal(false);
    setEditedGoalDescription('');
    setEditedGoalType('one_off');
    setGoalProposalActionError('');
  }, []);

  // Reset all goal states
  const resetGoalStates = useCallback(() => {
    // Clear AI goal proposal states
    clearGoalProposal();
    setGoalProposalActionLoading(false);
    
    // Clear manual goal states
    setIsManualGoalModalOpen(false);
    setManualGoalDescription('');
    setManualGoalType('one_off');
    setManualGoalError('');
    setManualGoalLoading(false);
  }, [clearGoalProposal]);

  // Handle therapist action on AI goal proposal
  const handleTherapistActionOnGoalProposal = useCallback(async (action) => {
    if (!pendingGoalProposal || !selectedConversation) return;

    let goalDetailsToSend = pendingGoalProposal.goalDetails;

    // If editing, use the edited version
    if (isEditingGoalProposal && action === 'approve') {
      goalDetailsToSend = {
        description: editedGoalDescription,
        goalType: editedGoalType
      };
    }

    setGoalProposalActionLoading(true);
    setGoalProposalActionError('');

    try {
      const payload = {
        action, // 'approve' or 'reject'
        goalDetails: goalDetailsToSend,
        originalAiText: pendingGoalProposal.originalAiText,
        originalFunctionCall: pendingGoalProposal.originalFunctionCall 
      };

      const response = await api.handleTherapistGoalProposalAction(selectedConversation._id, payload, token);
      console.log('Therapist action on goal proposal response:', response);
      
      clearGoalProposal(); // Clear the proposal after handling
      
      if (onGoalProposalHandled) {
        onGoalProposalHandled(action, goalDetailsToSend);
      }
    } catch (err) {
      console.error('Error handling therapist action on goal proposal:', err);
      setGoalProposalActionError(err.message || 'Failed to process action on goal proposal.');
      if (onError) {
        onError(err.message || 'Failed to process action on goal proposal.');
      }
    } finally {
      setGoalProposalActionLoading(false);
    }
  }, [
    pendingGoalProposal,
    selectedConversation,
    isEditingGoalProposal,
    editedGoalDescription,
    editedGoalType,
    token,
    clearGoalProposal,
    onGoalProposalHandled,
    onError
  ]);

  // Start editing AI goal proposal
  const handleStartEditingGoalProposal = useCallback(() => {
    if (!pendingGoalProposal) return;
    setEditedGoalDescription(pendingGoalProposal.goalDetails.description);
    setEditedGoalType(pendingGoalProposal.goalDetails.goalType || 'one_off');
    setIsEditingGoalProposal(true);
  }, [pendingGoalProposal]);

  // Cancel editing AI goal proposal
  const handleCancelEditingGoalProposal = useCallback(() => {
    setIsEditingGoalProposal(false);
    setEditedGoalDescription('');
    setEditedGoalType('one_off');
  }, []);

  // Manual Goal Modal Handlers
  const handleOpenManualGoalModal = useCallback(() => {
    setManualGoalDescription('');
    setManualGoalType('one_off');
    setManualGoalError('');
    setIsManualGoalModalOpen(true);
  }, []);

  const handleCloseManualGoalModal = useCallback(() => {
    setIsManualGoalModalOpen(false);
    setManualGoalError(''); // Clear error on close
  }, []);

  // Submit manual goal proposal
  const handleSubmitManualGoalProposal = useCallback(async () => {
    if (!manualGoalDescription.trim()) {
      setManualGoalError('Goal description cannot be empty.');
      return;
    }
    if (!selectedConversation) {
      setManualGoalError('No active conversation selected.');
      return;
    }
    
    setManualGoalLoading(true);
    setManualGoalError('');
    
    try {
      const goalData = {
        description: manualGoalDescription,
        goalType: manualGoalType,
      };
      await api.proposeManualTherapistGoal(selectedConversation._id, goalData, token);
      
      if (onSuccess) {
        onSuccess('Goal proposal sent to client successfully.');
      }
      
      handleCloseManualGoalModal();
    } catch (err) {
      console.error("Error proposing manual goal:", err);
      setManualGoalError(err.message || 'Failed to propose goal.');
    } finally {
      setManualGoalLoading(false);
    }
  }, [
    manualGoalDescription,
    manualGoalType,
    selectedConversation,
    token,
    onSuccess,
    handleCloseManualGoalModal
  ]);

  // Determine if manual goal proposal is allowed
  const canProposeManualGoal = userRole === 'therapist' && selectedConversation && 
    (currentMode === 'human' || (currentMode === 'assisted' && !pendingGoalProposal && !currentSuggestion));

  // Notify parent component when canProposeManualGoal changes
  useEffect(() => {
    if (onCanProposeManualGoalChange) {
      onCanProposeManualGoalChange(canProposeManualGoal);
    }
  }, [canProposeManualGoal, onCanProposeManualGoalChange]);

  // Check placeholder text conditions
  const isGoalModalOpen = isManualGoalModalOpen;
  const hasPendingGoalProposal = !!pendingGoalProposal;

  // Expose methods to parent component
  React.useImperativeHandle(ref, () => ({
    // AI Goal Proposal methods
    handleAiGoalProposal,
    clearGoalProposal,
    resetGoalStates,
    handleTherapistActionOnGoalProposal,
    
    // Manual Goal methods
    openManualGoalModal: handleOpenManualGoalModal,
    closeManualGoalModal: handleCloseManualGoalModal,
    
    // State getters
    hasPendingGoalProposal: () => {
      return !!pendingGoalProposal;
    },
    isGoalModalOpen: () => isGoalModalOpen,
    canProposeManualGoal: () => canProposeManualGoal,
    
    // Current values
    getPendingGoalProposal: () => {
      return pendingGoalProposal;
    },
    getGoalProposalActionLoading: () => goalProposalActionLoading,
    
    // Editing state methods for inline card
    getIsEditingGoalProposal: () => isEditingGoalProposal,
    getEditedGoalDescription: () => editedGoalDescription,
    getEditedGoalType: () => editedGoalType,
    setEditedGoalDescription,
    setEditedGoalType,
    handleStartEditingGoalProposal,
    handleCancelEditingGoalProposal
  }));

  return (
    <>
      {/* Goal Proposal Action Error */}
      {goalProposalActionError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {goalProposalActionError}
        </Alert>
      )}


      {/* Manual Goal Proposal Modal */}
      <ManualGoalModal
        isOpen={isManualGoalModalOpen}
        onClose={handleCloseManualGoalModal}
        description={manualGoalDescription}
        type={manualGoalType}
        onDescriptionChange={(e) => setManualGoalDescription(e.target.value)}
        onTypeChange={(e) => setManualGoalType(e.target.value)}
        onSubmit={handleSubmitManualGoalProposal}
        loading={manualGoalLoading}
        error={manualGoalError}
      />
    </>
  );
});

GoalManagementSystem.displayName = 'GoalManagementSystem';

export default GoalManagementSystem;